import { canAnchorHandleIncomingCall } from '@/lib/anchorEligibility'
import { isCallOrderEnded } from '@/lib/callOrderLifecycleGuard'
import { useAuthStore } from '@/store/authStore'
import { useCallStore } from '@/store/callStore'
import type { VideoCall } from '@/types'

export const INCOMING_AUTO_ACCEPT_DELAY_MS = 500

type AutoAcceptRunner = (incomingCall: VideoCall) => void | Promise<void>

const autoAcceptTimers = new Map<string, ReturnType<typeof setTimeout>>()

let autoAcceptRunner: AutoAcceptRunner = async (incomingCall) => {
  const { autoAcceptIncomingCall } = await import('@/hooks/useIncomingCallActions')
  await autoAcceptIncomingCall(incomingCall)
}

export function scheduleIncomingAutoAccept(incomingCall: VideoCall) {
  const orderID = incomingCall.order_id
  if (!orderID || autoAcceptSkipReason(incomingCall)) {
    return
  }
  if (autoAcceptTimers.has(orderID)) {
    return
  }
  clearIncomingAutoAccept(orderID)
  autoAcceptTimers.set(orderID, setTimeout(() => {
    autoAcceptTimers.delete(orderID)
    if (autoAcceptSkipReason(incomingCall)) {
      return
    }
    Promise.resolve(autoAcceptRunner(incomingCall)).catch(() => undefined)
  }, INCOMING_AUTO_ACCEPT_DELAY_MS))
}

export function clearIncomingAutoAccept(orderID: string) {
  const timer = autoAcceptTimers.get(orderID)
  if (!timer) {
    return
  }
  clearTimeout(timer)
  autoAcceptTimers.delete(orderID)
}

export function clearAllIncomingAutoAccepts() {
  autoAcceptTimers.forEach((timer) => clearTimeout(timer))
  autoAcceptTimers.clear()
}

function autoAcceptSkipReason(incomingCall: VideoCall) {
  const orderID = incomingCall.order_id
  if (!orderID) {
    return 'missing_order_id'
  }
  const anchor = useAuthStore.getState().anchor
  if (!anchor?.auto_accept_enabled) {
    return 'auto_accept_disabled'
  }
  if (!canAnchorHandleIncomingCall(anchor)) {
    return 'anchor_not_eligible'
  }
  if (isCallOrderEnded(orderID)) {
    return 'order_ended'
  }
  const calls = useCallStore.getState()
  if (calls.currentCall) {
    return 'current_call_active'
  }
  if (calls.outgoingCall) {
    return 'outgoing_call_active'
  }
  if (calls.incomingCall?.order_id !== orderID) {
    return 'incoming_changed'
  }
  if (calls.incomingActionOrderID) {
    return 'incoming_action_pending'
  }
  return ''
}

export const __incomingAutoAcceptTestHooks = {
  setRunner(runner: AutoAcceptRunner) {
    autoAcceptRunner = runner
  },
  resetRunner() {
    autoAcceptRunner = async (incomingCall) => {
      const { autoAcceptIncomingCall } = await import('@/hooks/useIncomingCallActions')
      await autoAcceptIncomingCall(incomingCall)
    }
  },
  pendingOrderIDs() {
    return Array.from(autoAcceptTimers.keys())
  },
}
